import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { FiLock, FiMail, FiEye, FiEyeOff, FiAlertCircle, FiPackage, FiArrowLeft, FiCheck } from 'react-icons/fi';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';

const DealerLogin = () => {
  const navigate = useNavigate();
  const { signIn, user, userRole } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [forgotMode, setForgotMode] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');

  // If already logged in, redirect to admin
  useEffect(() => {
    if (user && (userRole === 'dealer_admin' || userRole === 'dealer_staff' || userRole === 'master_admin')) {
      navigate('/admin');
    }
  }, [user, userRole, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { data, error: signInError } = await signIn(email, password);
      console.log('Sign in result:', { data, error: signInError });
      if (signInError) {
        const msg = signInError.message || '';
        if (msg.toLowerCase().includes('email not confirmed')) {
          setError('Your email has not been confirmed yet. Check your inbox for a confirmation link, or contact your ShedX administrator.');
        } else if (msg.toLowerCase().includes('invalid login credentials') || msg.toLowerCase().includes('invalid email or password')) {
          setError('Invalid email or password. Make sure you\'re using the credentials you created during onboarding.');
        } else {
          setError(msg || 'Login failed. Please try again.');
        }
      }
      // Navigation is handled by the useEffect watching user+userRole
    } catch (err) {
      console.error('Login error:', err);
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    setResetError('');

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/reset-password`
      });
      if (error) {
        setResetError(error.message);
      } else {
        setResetSent(true);
      }
    } catch (err) {
      setResetError('Failed to send reset email. Try again.');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{forgotMode ? 'Reset Password' : 'Dealer Login'} - ShedX</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-emerald-600 rounded-xl flex items-center justify-center mx-auto mb-4">
              <FiPackage className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Dealer Portal</h1>
            <p className="text-gray-500 mt-1 text-sm">
              Sign in to manage your shed inventory
            </p>
          </div>

          {/* Login Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            {forgotMode ? (
              resetSent ? (
                <div className="text-center py-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FiCheck className="w-6 h-6 text-emerald-600" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-2">Check your email</h2>
                  <p className="text-sm text-gray-500 mb-6">We sent a password reset link to <strong>{resetEmail}</strong></p>
                  <button
                    type="button"
                    onClick={() => { setForgotMode(false); setResetSent(false); setResetEmail(''); }}
                    className="text-sm text-emerald-600 hover:text-emerald-700 flex items-center gap-1 mx-auto"
                  >
                    <FiArrowLeft size={14} /> Back to login
                  </button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-5">
                  <div>
                    <button
                      type="button"
                      onClick={() => { setForgotMode(false); setResetError(''); }}
                      className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-4"
                    >
                      <FiArrowLeft size={14} /> Back to login
                    </button>
                    <h2 className="text-lg font-semibold text-gray-900 mb-1">Reset your password</h2>
                    <p className="text-sm text-gray-500">Enter your email and we'll send a reset link.</p>
                  </div>

                  {resetError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                      <FiAlertCircle className="flex-shrink-0" />
                      {resetError}
                    </div>
                  )}

                  <div>
                    <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FiMail className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        id="reset-email"
                        type="email"
                        required
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        placeholder="you@company.com"
                        className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={resetLoading}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white font-medium py-3 rounded-lg transition-colors text-sm"
                  >
                    {resetLoading ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                        Sending...
                      </div>
                    ) : (
                      'Send Reset Link'
                    )}
                  </button>
                </form>
              )
            ) : (
            <>
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                  <FiAlertCircle className="flex-shrink-0" />
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="dealer-email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FiMail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="dealer-email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="dealer-password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FiLock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="dealer-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full pl-10 pr-12 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                  </button>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => { setForgotMode(true); setResetEmail(email); setError(''); }}
                  className="text-xs text-emerald-600 hover:text-emerald-700 transition-colors"
                >
                  Forgot password?
                </button>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white font-medium py-3 rounded-lg transition-colors text-sm"
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Signing in...
                  </div>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>

            <div className="mt-6 pt-5 border-t border-gray-100 text-center">
              <p className="text-xs text-gray-400">
                Don't have an account? Contact your ShedX administrator for an invitation link.
              </p>
            </div>
            </>
            )}
          </div>

          {/* Back to site */}
          <p className="text-center mt-6">
            <a href="/" className="text-sm text-gray-400 hover:text-gray-600 transition-colors">
              &larr; Back to ShedX
            </a>
          </p>
        </motion.div>
      </div>
    </>
  );
};

export default DealerLogin;
